// Shared gesture-state contract between the SortableGrid shell and its three
// gesture hooks (internal drag, pointer-capture resize, external palette drag).
//
// State that drives rendering lives in ONE `GestureState` object behind a
// single `useState` (atomic transitions — never a half-updated frame), with a
// ref mirror written synchronously so pointer handlers read fresh values.
// Pointer-frequency scratch (metrics, start snapshot, last snapped cell) lives
// in ONE ref record and never triggers renders. Render cadence stays exactly
// "once per snapped-cell change".

import type * as React from 'react';

import type {
  GridFlow,
  GridLayoutItem,
  GridMetrics,
  GridRect,
  SpanConstraints,
} from './grid-layout';

export type ActiveMode = 'drag' | 'resize' | 'external' | 'marquee';

export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/** Phantom layout id an external (palette) drag occupies during preview —
 *  never rendered as a real tile, only as the dashed drop ghost. */
export const EXTERNAL_ID = '__external__';

/** Gesture id of a marquee (drag-select on open cells). Matches no tile, sets
 *  no preview — the selection rect renders from `GestureState.area`. */
export const MARQUEE_ID = '__marquee__';

export interface GestureState {
  /** Active tile id ({@link EXTERNAL_ID} for palette drags, {@link MARQUEE_ID}
   *  for marquees). */
  id: string;
  mode: ActiveMode;
  /** Original cell of a dragged tile — the anchor it translates from. */
  origin: GridLayoutItem | null;
  /** Live reflow preview; null until the pointer first crosses a cell edge. */
  preview: GridLayoutItem[] | null;
  /** Live marquee selection rect; null for non-marquee modes. */
  area: GridRect | null;
}

export interface GestureScratch {
  /** Grid metrics measured at gesture start. */
  metrics: GridMetrics | null;
  /** Layout snapshot at gesture start so deltas accumulate from a fixed origin
   *  rather than compounding frame to frame. */
  startLayout: GridLayoutItem[];
  /** Last snapped drag/external target cell — previews only recompute when the
   *  snapped cell changes. */
  lastTarget: { x: number; y: number } | null;
  /** Last snapped resize rect — same skip discipline for resizes. */
  lastResize: { x: number; y: number; w: number; h: number } | null;
  /** Palette drag payload + span, held for the drop callback. */
  externalItem: unknown;
  externalSpan: { w: number; h: number } | null;
}

export function emptyScratch(): GestureScratch {
  return {
    metrics: null,
    startLayout: [],
    lastTarget: null,
    lastResize: null,
    externalItem: undefined,
    externalSpan: null,
  };
}

/** Everything a gesture hook needs from the grid shell. Memoized against the
 *  infrequently-changing props (columns/flow/canManage); all callbacks are
 *  stable (they read live values through refs). */
export interface GridGestureCore {
  columns: number;
  flow: GridFlow;
  canManage: boolean;
  /** Treat the live-reflow modifier as always held (nested columns grids —
   *  displacement in a 1-wide column is local, and live reflow is what makes
   *  "drop commits exactly the preview" true). */
  liveReflow: boolean;
  /** This grid's own subgrid-zone id (`zone.id` on nested grids); null for
   *  page grids. Preview/drop routing keys off it: a grid previews iff the
   *  drag's `over` zone matches (`null` = "not over any enabled zone"). */
  zoneId: string | null;
  layoutRef: React.MutableRefObject<GridLayoutItem[]>;
  gestureRef: React.MutableRefObject<GestureState | null>;
  scratch: React.MutableRefObject<GestureScratch>;
  /** Replace the whole gesture state (writes the ref synchronously, then the
   *  render state). */
  setGesture: (next: GestureState | null) => void;
  /** Update only the preview of the in-flight gesture. */
  setPreview: (preview: GridLayoutItem[] | null) => void;
  /** Update only the marquee selection rect of the in-flight gesture. */
  setArea: (area: GridRect | null) => void;
  gridEl: () => HTMLElement | null;
  measure: () => GridMetrics | null;
  constraintsFor: (id: string) => SpanConstraints;
  /** Commit the in-flight preview (if any) through `onLayoutChange`, clear. */
  commitGesture: () => void;
  /** Clear the gesture without committing. */
  cancelGesture: () => void;
}
